import { useEffect, useState, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useParams, Link } from 'react-router-dom';
import axios from "../../axios-config";
import { ArrowLeft, PackageSearch, ShoppingBasket, ShoppingCart, Star, Tag, Sparkles, Coins, ShoppingBag, History, Clock, CheckCircle, XCircle, Truck, Delete, Trash, Plus, Lock, MapPin } from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import { BadgeContext } from '../../contexts/BadgeContext';
import ShopManagement from '../Store/ShopManagement';
import { AcornOrderStatuses } from '../../types/enums'; // New import
import { startNotificationConnection, OrderNotification } from '../../services/notificationHubService';
import { LoadingContext } from '../../contexts/LoadingContext';
import mediaService from '../../services/mediaService';

type OrderStatus = 'processing' | 'ready' | 'completed' | 'cancelled';
// type PickupLocation = 'main-office' | 'library' | 'cafeteria' | 'student-store'; // Changed to string for flexibility

type Order = {
  id: string;
  storeId?: number; // Made optional
  date: string;
  items: {
    name: string;
    points: number;
    quantity: number;
    imageUrl?: string;
  }[];
  status: OrderStatus;
  pickupCode: string;
  total: number;
  pickupLocation: string; // Changed to string
  acornOrderStatusId?: number; // Added to store original status ID
};

const mapOrderStatusIdToStatus = (statusId?: number): OrderStatus => {
  if (statusId === undefined) return 'processing';
  switch (statusId) {
    case AcornOrderStatuses.Processing:
      return 'processing';
    case AcornOrderStatuses.Pending: // This means "Ready for Pickup"
      return 'ready'; // Corrected: Was 'processing'
    case AcornOrderStatuses.Fulfilled:
      return 'completed';
    case AcornOrderStatuses.PartiallyFulfilled: // Assuming this also means items are ready
      return 'ready';
    case AcornOrderStatuses.Cancelled:
      return 'cancelled';
    default:
      return 'processing';
  }
};

// Helper function to get item name with robust fallback logic
const getItemName = (item: any): string => {
  // Debug logging to understand the actual data structure
  console.log('getItemName - item data:', item);
  
  // Try multiple possible data structures from API response
  if (item.products?.productName) {
    return item.products.productName;
  } else if (item.product?.productName) {
    return item.product.productName;
  } else if (item.productName) {
    return item.productName;
  } else if (item.name) {
    return item.name;
  } else if (item.product?.name) {
    return item.product.name;
  } else if (item.products?.name) {
    return item.products.name;
  }
  
  // If all else fails, log the structure and return fallback
  console.warn('getItemName - Could not find item name in:', item);
  return 'Unknown item';
};

// Updated transformAcornOrder to use item.products for name and imageUrl, and handle new status logic.
const transformAcornOrder = (orderData: any): Order => {
  const items = (orderData.acornOrderItem || []).map((item: any) => {
    return {
      name: getItemName(item),
      points: item.pointCost || 0,
      quantity: item.quantity || 1,
      imageUrl: item.products?.imageUrl || item.imageUrl || ""
    };
  });

  const calculatedTotal = items.reduce((sum: number, item: any) => sum + (item.points * item.quantity), 0);

  let status: OrderStatus;
  if (orderData.acornOrderStatusId !== undefined) {
    status = mapOrderStatusIdToStatus(orderData.acornOrderStatusId);
  } else if (orderData.acornOrderStatus && orderData.acornOrderStatus.acornOrderStatusName) {
    // Fallback for old API structure if acornOrderStatusName is present
    const statusName = (orderData.acornOrderStatus.acornOrderStatusName.toLowerCase());
    const validStatuses: OrderStatus[] = ['processing', 'ready', 'completed', 'cancelled'];
    if (validStatuses.includes(statusName as OrderStatus)) {
      status = statusName as OrderStatus;
    } else {
      status = 'processing'; // Default for unrecognized status names
    }
  } else {
    // Default if no status information is provided in userProfile.orders
    status = mapOrderStatusIdToStatus(AcornOrderStatuses.Processing);
  }

  return {
    id: String(orderData.acornOrderId || orderData.id), // Ensure ID is a string
    storeId: orderData.storeId,
    date: orderData.acornOrderDate || new Date().toISOString(),
    items: items,
    status: status,
    total: orderData.totalPoints || orderData.acornTransactions?.reduce((sum: number, txn: any) => sum + (txn.amount || 0), 0) || calculatedTotal,
    pickupLocation: orderData.pickupLocation || 'student-store', // Default pickup location
    pickupCode: orderData.pickupCode || '',
    acornOrderStatusId: orderData.acornOrderStatusId
  };
};

type Product = {
  productId: number;
  storeId: number;
  productName: string;
  productDescription?: string;
  imageUrl?: string;
  pointCost: number;
  stockQuantity?: number;
  productUnlimited?: boolean;
  maxPerStudent?: number;
  pickupLocation?: string;
  createdAt?: Date;
  createdBy?: string;
  updatedAt?: Date;
  requiredBadgeId?: string;
  requiredBadgeLevel?: number;
};

export default function Shop() {
  // Accept either /:storeSlug or /:slug
  const params = useParams<{ storeSlug?: string; slug?: string }>();
  const storeSlug = params.storeSlug ?? params.slug ?? '';   // ← unified slug

  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [cartCount, setCartCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'shop' | 'history' | 'manage-products' | 'manage-orders'>('shop');
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false); // New state
  const { userProfile, setUserProfile, hasClaim } = useContext(UserContext);
  const { badges } = useContext(BadgeContext);
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!storeSlug) return;                // ← wait until slug is present
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`/api/Products/Slug/${storeSlug}`);
        setProducts(res.data);
      } catch (err: any) {
        toast.error(String(err));
      }
    };
    fetchProducts();
  }, [storeSlug]);

  useEffect(() => {
    // Load and sort orders from userProfile
    if (userProfile?.orders?.length) {
      const transformedOrders = userProfile.orders
        .map((o: any) => transformAcornOrder(o))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // ← newest first
      setOrders(transformedOrders);
    } else {
      setOrders([]);
    }
  }, [userProfile]); // Depend on userProfile to re-process orders when it changes

  // ★ subscribe once to real-time order updates
  useEffect(() => {
    startNotificationConnection(
      /* hallPass */ undefined,
      /* context  */ 'nonStudent',
      (payload: OrderNotification) => {
        if (payload.type === 'ready') {
          setOrders(prev =>
            prev.map(o =>
              o.id === String(payload.orderId)
                ? {
                  ...o,
                  status: 'ready',
                  pickupCode: payload.pickupCode,
                  pickupLocation: payload.pickupLocation
                }
                : o
            )
          );
        }
      }
    );
  }, []);

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'processing':
        return 'text-blue-600 bg-blue-50';
      case 'ready':
        return 'text-green-600 bg-green-50';
      case 'completed':
        return 'text-gray-600 bg-gray-50';
      case 'cancelled':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'processing':
        return <Clock className="w-4 h-4" />;
      case 'ready':
        return <CheckCircle className="w-4 h-4" />;
      case 'completed':
        return <Truck className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // ────────────────────────────────────────────────────────────
  // 1. how many of each product the user has already purchased
  const purchasedCounts = useMemo<Record<string, number>>(() => {
    const tally: Record<string, number> = {};
    orders
      .filter(o => o.status !== 'cancelled')          // ignore cancelled
      .forEach(o =>
        o.items.forEach(it => {
          tally[it.name] = (tally[it.name] ?? 0) + it.quantity;
        })
      );
    return tally;
  }, [orders]);
  // helper – remaining quantity the student may still buy
  const remainingStudentLimit = (product: Product) => {
    if (product.maxPerStudent == null) return Infinity; // unlimited
    const purchased = purchasedCounts[product.productName] ?? 0;
    const inCart = cartItems.find(c => c.productId === product.productId)?.quantity ?? 0;
    return Math.max(product.maxPerStudent - purchased - inCart, 0);
  };
  // ────────────────────────────────────────────────────────────

  // New: Update item quantity (now respects per-student limit)
  const updateCartItemQuantity = (productId: number, delta: number) => {
    setCartItems(prev => {
      const prod = products.find(p => p.productId === productId);
      if (!prod) return prev;

      // deny if exceeding personal limit
      if (delta > 0 && remainingStudentLimit(prod) <= 0) {
        toast.info(`Limit of ${prod.maxPerStudent} per student reached`);
        return prev;
      }

      return prev.reduce((acc, item) => {
        if (item.productId === productId) {
          const newQty = item.quantity + delta;
          if (newQty > 0) acc.push({ ...item, quantity: newQty });
        } else acc.push(item);
        return acc;
      }, [] as any[]);
    });
    setCartCount(prev => Math.max(prev + delta, 0));
  };

  // Add a new function to remove all items of a product from the cart.
  const removeCartItem = (productId: number) => {
    setCartItems(prev => {
      const itemToRemove = prev.find(item => item.productId === productId);
      if (itemToRemove) setCartCount(prevCount => Math.max(prevCount - itemToRemove.quantity, 0));
      return prev.filter(item => item.productId !== productId);
    });
  };

  // New: Handle actual checkout after confirmation and update user's balance
  const submitCheckout = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    startLoading();
    setCheckoutError(null); // Reset any previous errors

    try {
      const payload = {
        acornOrderId: 0,
        userId: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
        acornOrderDate: new Date().toISOString(),
        pickupLocation: "student-store",
        storeId: products[0].storeId,
        acornOrderItem: cartItems.map(item => ({
          productId: item.productId,
          storeId: item.storeId,
          pointCost: item.pointCost,
          quantity: item.quantity
        }))
      };

      const res = await axios.post('/api/Orders', payload);

      const { order: newOrderRaw, points: updatedPoints, general } = res.data;
      const newOrder = newOrderRaw ? transformAcornOrder(newOrderRaw) : null;

      toast.success('Checkout successful');

      // Update user profile (prepend new raw order)
      if (userProfile && setUserProfile) {
        setUserProfile({
          ...userProfile,
          points: updatedPoints,
          general,
          orders: newOrderRaw ? [newOrderRaw, ...(userProfile.orders ?? [])] : userProfile.orders
        });
      }

      // Prepend & sort in local state
      setOrders(prev => {
        const list = newOrder ? [newOrder, ...prev] : prev;
        return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      });

      // Clear cart state
      setCartCount(0);
      setCartItems([]);
      setShowCheckoutModal(false);
    } catch (err: any) {
      toast.error('Checkout error', err);
      // Extract error message from response if available
      const errorMessage = err.response?.data?.message || 'An error occurred during checkout';
      setCheckoutError(errorMessage);
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    startLoading();
    try {
      const res = await axios.put(`/api/Orders/${orderId}/cancel`);
      const { points: updatedPoints, general, order: cancelledOrder } = res.data;

      toast.success('Order cancelled successfully!');

      // --- NEW: update userProfile.orders so the useEffect will not revert the UI ---
      let updatedOrdersRaw = userProfile?.orders ?? [];
      if (userProfile && Array.isArray(updatedOrdersRaw)) {
        updatedOrdersRaw = updatedOrdersRaw.map(o =>
          String(o.acornOrderId ?? o.id) === orderId
            ? (cancelledOrder ?? { ...o, acornOrderStatusId: AcornOrderStatuses.Cancelled })
            : o
        );
      }

      if (userProfile && setUserProfile) {
        setUserProfile({
          ...userProfile,
          points: updatedPoints,
          general,
          orders: updatedOrdersRaw,   // keep orders in sync
        });
      }

      // keep local state in sync as before
      setOrders(prev =>
        prev.map(o =>
          o.id === orderId
            ? cancelledOrder
              ? transformAcornOrder(cancelledOrder)
              : { ...o, status: 'cancelled' }
            : o
        )
      );
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to cancel order.');
    } finally {
      stopLoading();
    }
  };

  return (
    <>
      {/* Header */}
      <div className="max-w-6xl mx-auto px-6">
        {/* Shop Header */}
        <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="p-2 hover:bg-gray-100/80 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-800">{userProfile?.storeName || "Student Store"}</h1>
            </div>
            <div className="flex items-center gap-3">
              {/* <div className="flex items-center gap-2">
                <button
                  className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
                  title="Log out"
                >
                  <LogOut className="w-5 h-5 text-gray-600" />
                </button>
              </div> */}
              <div className="relative">
                <button className="relative p-2 hover:bg-gray-100/80 rounded-lg transition-colors"
                  onClick={() => setShowCheckoutModal(true)}
                  title="Checkout"
                >
                  <ShoppingCart className="w-5 h-5 text-gray-600" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-indigo-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>
              </div>
              <div className="flex items-center gap-2 bg-gray-100/80 text-gray-700 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                <Tag className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-medium">
                  {userProfile?.points ? `${userProfile.points.toLocaleString()} pts` : "0 pts"}
                </span>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 flex flex-col gap-4">
            {/* Tab Navigation */}
            <div className="flex gap-4 border-b border-gray-200/50">
              <button
                onClick={() => setActiveTab('shop')}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'shop'
                  ? 'text-indigo-600'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4" />
                  Shop
                </div>
                {activeTab === 'shop' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'history'
                  ? 'text-teal-600'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Orders
                </div>
                {activeTab === 'history' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                )}

              </button>
              {hasClaim("CanManageStore") && (
                <>
                  <button
                    onClick={() => setActiveTab('manage-products')}
                    className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'manage-products'
                        ? 'text-purple-600'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <PackageSearch className="w-4 h-4" />
                      Manage Products
                    </div>
                    {activeTab === 'manage-products' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('manage-orders')}
                    className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'manage-orders'
                        ? 'text-purple-600'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <ShoppingBasket className="w-4 h-4" />
                      Manage Orders
                    </div>
                    {activeTab === 'manage-orders' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'history' ? (
          <>
            <div className="flex items-center gap-2 mb-6">
              <History className="w-5 h-5 text-teal-500" />
              <h2 className="text-xl font-semibold text-gray-800">Orders</h2>
            </div>
            <div className="space-y-4">
              {orders.map(order => (
                <div
                  key={order.id}
                  className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-800">Order {order.id}</h3>
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          Ordered on {new Date(order.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-1 bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg self-start">
                          <Coins className="w-4 h-4 text-yellow-500" />
                          <span className="font-medium">{order.total} pts</span>
                        </div>
                        {(order.status === 'processing' || order.status === 'ready') && (
                          <button
                            onClick={() => handleCancelOrder(order.id)}
                            className="mt-2 px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            Cancel Order
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      {order.items.map((item, index) => {
                        const fallbackProduct = products.find(product => product.productName === item.name);
                        let imageSource: string | null = null;
                        if (item.imageUrl) {
                          imageSource = mediaService.getDirectUrl(userProfile?.orgId || '', String(item.imageUrl));
                        } else if (fallbackProduct?.imageUrl) {
                          imageSource = mediaService.getDirectUrl(userProfile?.orgId || '', String(fallbackProduct.imageUrl));
                        }
                        return (
                          <div key={index} className="flex items-center gap-4 py-2 border-t border-gray-100 first:border-0">
                            {imageSource && (
                              <img
                                src={imageSource}
                                alt={item.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            )}
                            <div className="flex-1">
                              <div className="text-gray-700 font-semibold">{item.name}</div>
                              <div className="text-sm text-gray-500">Qty: {item.quantity}</div>
                            </div>
                            <div className="text-sm text-gray-600">
                              {item.points * item.quantity} pts
                            </div>
                          </div>
                        );
                      })}
                      <div className="mt-4 space-y-3">
                        <div className={`p-3 rounded-lg ${order.status === 'ready' ? 'bg-green-50' :
                          order.status === 'completed' ? 'bg-gray-50' :
                            order.status === 'cancelled' ? 'bg-red-50' :
                              'bg-blue-50'
                          }`}>
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-medium ${order.status === 'ready' ? 'text-green-700' :
                              order.status === 'completed' ? 'text-gray-700' :
                                order.status === 'cancelled' ? 'text-red-700' :
                                  'text-blue-700'
                              }`}>Pickup Code:</span>
                            <span className={`font-mono font-bold tracking-wider ${order.status === 'ready' ? 'text-green-700' :
                              order.status === 'completed' ? 'text-gray-700' :
                                order.status === 'cancelled' ? 'text-red-700' :
                                  'text-blue-700'
                              }`}>
                              {order.pickupCode}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${order.status === 'ready' ? 'text-green-600' :
                            order.status === 'completed' ? 'text-gray-600' :
                              order.status === 'cancelled' ? 'text-red-600' :
                                'text-blue-600'
                            }`}>
                            {order.status === 'ready' && 'Show this code to pick up your items'}
                            {order.status === 'completed' && 'Order picked up with this code'}
                            {order.status === 'cancelled' && 'Order cancelled - code invalid'}
                            {order.status === 'processing' && 'Code will be active when order is ready'}
                          </p>
                        </div>
                        <div className={`p-3 rounded-lg ${order.status === 'ready' ? 'bg-blue-50' :
                          order.status === 'completed' ? 'bg-gray-50' :
                            order.status === 'cancelled' ? 'bg-red-50/50' :
                              'bg-blue-50/50'
                          }`}>
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-medium ${order.status === 'ready' ? 'text-blue-700' :
                              order.status === 'completed' ? 'text-gray-700' :
                                order.status === 'cancelled' ? 'text-red-700/75' :
                                  'text-blue-700/75'
                              }`}>Pickup Location:</span>
                            <span className={`font-medium capitalize ${order.status === 'ready' ? 'text-blue-700' :
                              order.status === 'completed' ? 'text-gray-700' :
                                order.status === 'cancelled' ? 'text-red-700/75' :
                                  'text-blue-700/75'
                              }`}>
                              {order.pickupLocation ? order.pickupLocation.split('-').join(' ') : 'N/A'}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${order.status === 'ready' ? 'text-blue-600' :
                            order.status === 'completed' ? 'text-gray-600' :
                              order.status === 'cancelled' ? 'text-red-600/75' :
                                'text-blue-600/75'
                            }`}>
                            {order.pickupLocation === 'main-office' && 'Located in Building A, Room 101'}
                            {order.pickupLocation === 'library' && 'At the library front desk'}
                            {order.pickupLocation === 'cafeteria' && 'At the cafeteria service counter'}
                            {order.pickupLocation === 'student-store' && 'At the student store counter'}
                            {/* Add a generic message if none of the above match and location is present */}
                            {order.pickupLocation && !['main-office', 'library', 'cafeteria', 'student-store'].includes(order.pickupLocation.toLowerCase().split(' ').join('-')) && `Please check details for pickup at: ${order.pickupLocation.split('-').join(' ')}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : activeTab === 'manage-products' ? (
          <ShopManagement activeTab='products' />
        ) : activeTab === 'manage-orders' ? (
          <ShopManagement activeTab='orders' />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <h2 className="text-xl font-semibold text-gray-800">Items</h2>
              </div>
              <button
                onClick={() => setShowCheckoutModal(true)} // open modal
                className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                Checkout
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map(product => {
                const cartItem = cartItems.find(ci => ci.productId === product.productId);
                const available = Math.max((product.stockQuantity ?? 0) - (cartItem?.quantity ?? 0), 0);
                const userBadgeLevel = userProfile?.badges
                  ?.find(b => b.badgeId === product.requiredBadgeId)
                  ?.levelNumber ?? 0;
                const requiresBadge = Boolean(product.requiredBadgeId && product.requiredBadgeLevel);
                const meetsBadge = !requiresBadge
                  || userBadgeLevel >= (product.requiredBadgeLevel ?? 0);

                // find badge metadata for tooltip
                const requiredBadge = badges.find(b => b.id === product.requiredBadgeId);
                const levelNames = requiredBadge
                  ? Object.entries(requiredBadge.levelThresholds)
                      .sort((a, b) => a[1] - b[1])
                      .map(([name]) => name)
                  : [];
                const requiredLevelName =
                  product.requiredBadgeLevel && product.requiredBadgeLevel > 0 && product.requiredBadgeLevel <= levelNames.length
                    ? levelNames[product.requiredBadgeLevel - 1]
                    : undefined;
                const requiredXp = requiredLevelName && requiredBadge
                  ? requiredBadge.levelThresholds[requiredLevelName]
                  : 0;
                const currentXp = userProfile?.badges
                  ?.find(b => b.badgeId === product.requiredBadgeId)
                  ?.currentXp ?? 0;
                const remainingXp = Math.max(requiredXp - currentXp, 0);

                return (
                  <div
                    key={product.productId}
                    className="relative bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="relative">
                      <img
                        src={product.imageUrl?.startsWith('https://') 
                          ? product.imageUrl 
                          : mediaService.getDirectUrl(userProfile?.orgId || '', String(product.imageUrl))}
                        alt={product.productName}
                        className="w-full h-48 object-cover"
                      />
                      {/* Badge lock icon overlay - only if badge requirement unmet */}
                      {!meetsBadge && (
                        <div className="absolute top-2 right-2 bg-gray-900/80 backdrop-blur-sm rounded-full p-2">
                          <Lock className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">{product.productName}</h3>
                        <span className="flex items-center gap-1 bg-indigo-50 text-indigo-600 px-2 py-1 rounded-lg text-sm font-medium">
                          <Star className="w-4 h-4" />
                          {product.pointCost} pts
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">{product.productDescription}</p>
                      
                      {/* Product details section */}
                      <div className="space-y-2 mb-4 text-sm">
                        {/* In Stock */}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">In Stock</span>
                          <span className="font-medium text-gray-800">
                            {product.productUnlimited 
                              ? 'Unlimited' 
                              : typeof product.stockQuantity === 'number'
                                ? `${product.stockQuantity}${cartItem ? ` (${available} available)` : ''}`
                                : 'Contact store'
                            }
                          </span>
                        </div>
                        
                        {/* Max Per Student */}
                        {product.maxPerStudent && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Max Per Student</span>
                            <span className="font-medium text-gray-800">
                              Limit {product.maxPerStudent}
                            </span>
                          </div>
                        )}
                        
                        {/* Pickup Location */}
                        {product.pickupLocation && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Pickup Location</span>
                            <span className="font-medium text-gray-800 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {product.pickupLocation}
                            </span>
                          </div>
                        )}
                        
                        {/* Badge Lock Status */}
                        {requiresBadge && requiredBadge && (
                          <div className="flex items-start justify-between">
                            <span className="text-gray-600">Badge Locked</span>
                            <span className={`font-medium text-right flex-1 ml-2 ${meetsBadge ? 'text-green-600' : 'text-amber-600'}`}>
                              {meetsBadge ? (
                                `${requiredBadge.name}${requiredLevelName ? ` - ${requiredLevelName}` : ''}`
                              ) : (
                                `Earn ${remainingXp.toLocaleString()} more XP to reach ${requiredBadge.name}${requiredLevelName ? ` - ${requiredLevelName}` : ''}`
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {cartItem && (
                          <button
                            onClick={() => removeCartItem(product.productId)}
                            className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-1"
                          >
                            <Delete className="w-5 h-5" />
                            <span>Delete</span>
                          </button>
                        )}
                        <button
                          disabled={
                            !meetsBadge
                            || (!product.productUnlimited && available === 0)
                          }
                          onClick={() => {
                            if (!product.productUnlimited) {
                              if (available === 0) return;
                              if (product.maxPerStudent && (cartItem?.quantity ?? 0) >= product.maxPerStudent) {
                                toast.info(`Limit of ${product.maxPerStudent} per student reached`);
                                return;
                              }
                            }
                            setCartItems(prev => {
                              const idx = prev.findIndex(item => item.productId === product.productId);
                              if (idx !== -1) {
                                const updated = [...prev];
                                updated[idx].quantity += 1;
                                return updated;
                              }
                              return [...prev, {
                                productId: product.productId,
                                storeId: product.storeId,
                                pointCost: product.pointCost,
                                quantity: 1
                              }];
                            });
                            setCartCount(prev => prev + 1);
                          }}
                          className="flex-1 bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1 transition-colors hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-5 h-5" />
                          <span>{cartItem ? `Add to ${cartItem.quantity}` : "Add to Cart"}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowCheckoutModal(true)} // open modal
                className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                Checkout
              </button>
            </div>
          </>)}
      </div>

      {/* New: Modal Popup for checkout verification */}
      {showCheckoutModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Review Your Cart</h3>

            {/* Display checkout error if present */}
            {checkoutError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
                <p className="font-medium">{checkoutError}</p>
              </div>
            )}

            <div className="max-h-60 overflow-y-auto">
              {cartItems.map(item => {
                const product = products.find(p => p.productId === item.productId);
                return (
                  <div key={item.productId} className="flex items-center gap-4 mb-4 border-b pb-2">
                    {product && product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.productName} className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded" />
                    )}
                    <div className="flex-1">
                      <div className="font-semibold">{product ? product.productName : 'Product'}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <button onClick={() => updateCartItemQuantity(item.productId, -1)} className="px-2 bg-gray-200 rounded">-</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateCartItemQuantity(item.productId, 1)} className="px-2 bg-gray-200 rounded">+</button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeCartItem(item.productId)}
                      className="bg-red-500 text-white text-sm flex items-center gap-1 rounded-full p-2"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              {cartItems.length === 0 && <p>Your cart is empty.</p>}
            </div>

            {/* Add order summary section */}
            {cartItems.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="font-medium mb-3">Order Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Total:</span>
                    <span className="font-medium">
                      {cartItems.reduce((total, item) => total + (item.pointCost * item.quantity), 0)} pts
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Balance:</span>
                    <span className="font-medium">{userProfile?.points?.toLocaleString() || 0} pts</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <span className="font-medium">Balance After Purchase:</span>
                    <span className={`font-bold ${(userProfile?.points || 0) - cartItems.reduce((total, item) => total + (item.pointCost * item.quantity), 0) < 0
                        ? 'text-red-600'
                        : 'text-green-600'
                      }`}>
                      {((userProfile?.points || 0) - cartItems.reduce((total, item) => total + (item.pointCost * item.quantity), 0)).toLocaleString()} pts
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowCheckoutModal(false);
                  setCheckoutError(null); // Clear errors when closing
                }}
                className="px-4 py-2 rounded bg-gray-300 text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={submitCheckout}
                disabled={
                  isSubmitting ||
                  (userProfile?.points || 0) < cartItems.reduce((total, item) => total + (item.pointCost * item.quantity), 0)
                }
                className="px-4 py-2 rounded bg-green-500 hover:bg-green-600 text-white"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}